/**
 * @file    trendMapping.ts
 * @scope   SHARED
 * @summary Restores the selected chart trend from a saved widget config when re-opening a widget
 *          for editing (Enterprise widgets store the trend in queryCollections[0].trend).
 * @consumes SelectTrendInput `TrendOptions`, `CustomConfig`
 * @produces `restoreSelectedChartTrend`
 * @sideEffects None — pure lookup + merge.
 * @notes Injected into `useChartTrend` (DiyDrawer) as the enterprise restore step, so the drawer
 *        never imports the DiyEnterprise domain directly.
 */
import { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { CustomConfig } from 'models/WidgetModel';

/**
 * Restores the selected chart trend from saved widget config when re-opening a widget for editing.
 * For enterprise widgets (ManyCalculations), the trend is stored in queryCollections[0].trend.
 * Matches the resolved timePeriodType against the available trend options
 * and merges the saved selectedValue.
 * @param options - Available trend options built from the API response
 * @param customWidgetConfig - The saved widget config to extract the trend source from
 * @returns The matching TrendOptions with the restored selectedValue, or undefined if no match
 */
export const restoreSelectedChartTrend = (
  options: TrendOptions[],
  customWidgetConfig: CustomConfig | undefined
): TrendOptions | undefined => {
  const qc0: any = customWidgetConfig?.queryCollections?.[0];
  // WHY the widget-level fallback: CPR persists `trend` as a 1-item array on the config itself.
  const widgetTrend: any = customWidgetConfig?.trend;
  const savedTrend: any = qc0?.trend ?? (Array.isArray(widgetTrend) ? widgetTrend[0] : undefined);
  if (!savedTrend) return undefined;

  const matchingOption =
    options.find(
      (o) =>
        String(o.trend.timePeriodType).toLowerCase() ===
        String(savedTrend.timePeriodType).toLowerCase()
    ) ??
    (typeof savedTrend.timePeriodType === 'number'
      ? options[savedTrend.timePeriodType]
      : undefined);

  if (!matchingOption) return undefined;
  return {
    ...matchingOption,
    trend: {
      ...matchingOption.trend,
      selectedValue: savedTrend.selectedValue ?? savedTrend.interval,
    },
  };
};
